import { useEffect, useContext } from "react";
import { useSchema } from "../contexts/schema.context";
import { SchemaRegistry } from "../../services/schema/registry/schema.registry";
import { AppCtx } from "../contexts/app.context";
import {
  getAllBrandPath,
  getPathByTypeAndGender,
  toAbsoluteUrl,
} from "../utils/getBaseUrl";
import { AlgoliaSearchBrand } from "../types/Algolia";
import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { startCase } from "lodash-es";

interface CollectionPageSchemaProps {
  brand?: AlgoliaSearchBrand;
  category?: string;
  gender: string;
  slug: string;
  title?: string;
  description?: string;
  breadcrumbs?: { name: string; url?: string }[];
}

/**
 * Custom hook for managing collection page schema generation
 * @param props - Collection page data including brand, gender, slug, and breadcrumbs
 */
export const useCollectionPageSchema = ({
  brand,
  category,
  gender,
  slug,
  title,
  description,
  breadcrumbs,
}: CollectionPageSchemaProps): void => {
  const { addSchemas, removeSchemas } = useSchema();
  const { locale } = useContext(AppCtx);
  const { i18n } = useLingui();

  useEffect(() => {
    const schemaId = `collection-page-${gender}-${slug}`;

    if ((!brand && !category) || !gender || !slug) {
      removeSchemas(schemaId);
      return;
    }

    // Generate the collection page URL
    let collectionPageUrl = getPathByTypeAndGender({
      type: brand ? "brand" : "category",
      gender: gender?.toLowerCase() || "women",
      slug,
      locale,
    });

    if (collectionPageUrl) {
      collectionPageUrl = toAbsoluteUrl(collectionPageUrl);
    }
    const collectionName = brand ? brand.name : slug;

    // Generate description for the collection page
    const collectionDescription =
      description ||
      t(i18n)`Shop ${collectionName} ${gender} collection at Otrium.`;

    const collectionBreadcrumbs: { name: string; url?: string }[] =
      breadcrumbs || [
        {
          name: "Home",
          url: toAbsoluteUrl(`/${gender?.toLowerCase() || "women"}`),
        },
        {
          name: t(i18n)`All brands`,
          url: toAbsoluteUrl(
            `${gender?.toLowerCase() || "women"}/${getAllBrandPath(locale)}`
          ),
        },
        { name: collectionName, url: collectionPageUrl },
      ];

    // Generate schemas using the registry
    const schemas = SchemaRegistry.collectionPage({
      collectionPage: {
        name:
          title ||
          `${startCase(collectionName.replaceAll("-", " "))} - ${startCase(
            gender
          )}`,
        description: collectionDescription,
        url: collectionPageUrl || "",
        inLanguage: locale,
        breadcrumbs: collectionBreadcrumbs,
      },
    });

    addSchemas(schemas, schemaId);

    // Cleanup on unmount
    return () => removeSchemas(schemaId);
  }, [brand, gender, slug, locale, addSchemas, removeSchemas]);
};
